'use client'

import { useState, useCallback, useRef, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { type FunnelConfig, getApiBaseUrl } from '@/lib/hooks/useFunnelConfig'
import { useWishlist } from '@/lib/hooks/useWishlist'
import { WishlistEmailPopup } from '@/components/Configurator/WishlistEmailPopup'
import { WishlistSidebar } from '@/components/Configurator/WishlistSidebar'
import { SwipeCard, type SwipeDirection } from './SwipeCard'
import { LikesDrawer } from './LikesDrawer'
import { useSwiperState, type StoredLike } from './useSwiperState'
import { ct } from '@/lib/i18n/configurator'

interface SwiperProps {
  /** Comma-separated funnel slugs (e.g. "config-s,config-m,config-l") */
  slugs: string
}

/** Fetch multiple funnel configs in parallel */
function useMultiFunnelConfig(slugs: string[]) {
  const [configs, setConfigs] = useState<FunnelConfig[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const fetchedRef = useRef(false)

  useEffect(() => {
    if (slugs.length === 0 || fetchedRef.current) return
    fetchedRef.current = true

    const baseUrl = getApiBaseUrl()

    Promise.all(
      slugs.map(async slug => {
        const res = await fetch(`${baseUrl}/api/funnel/${slug}`)
        if (!res.ok) return null
        return res.json() as Promise<FunnelConfig>
      })
    )
      .then(results => {
        const valid = results.filter((c): c is FunnelConfig => c !== null)
        setConfigs(valid)
        if (valid.length === 0) setIsError(true)
      })
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugs.join(',')])

  return { configs, isLoading, isError }
}

export function Swiper({ slugs: slugsProp }: SwiperProps) {
  const slugList = slugsProp.split(',').map(s => s.trim()).filter(Boolean)
  const { configs, isLoading, isError } = useMultiFunnelConfig(slugList)

  // Wishlist uses first config's funnelId as fallback - super-likes store funnelId per combo
  const swiper = useSwiperState(configs)
  const currentCombo = swiper.currentCombination

  // Wishlist for the current combination's funnel
  const wishlist = useWishlist(currentCombo?.funnelId ?? configs[0]?.id, currentCombo?.funnelSlug ?? slugList[0])

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [mobileWishlistOpen, setMobileWishlistOpen] = useState(false)
  const pendingSuperLikeRef = useRef<{ wcIds: number[]; totalPrice: number; funnelId: string } | null>(null)
  const pendingWishlistLikeRef = useRef<StoredLike | null>(null)

  const saveToWishlist = useCallback(async (wcIds: number[], totalPrice: number, targetFunnelId: string) => {
    if (!wishlist.hasEmail) {
      pendingSuperLikeRef.current = { wcIds, totalPrice, funnelId: targetFunnelId }
      wishlist.requestEmail()
      return
    }
    await wishlist.addToWishlist(wcIds, totalPrice, targetFunnelId)
  }, [wishlist])

  const handleSwipe = useCallback((direction: SwipeDirection) => {
    const combo = swiper.currentCombination
    if (!combo) return

    const action = swiper.handleSwipe(direction)

    if (action === 'superlike') {
      saveToWishlist(combo.wcIds, combo.totalPrice, combo.funnelId)
    }
  }, [swiper, saveToWishlist])

  const handleDrawerAddToWishlist = useCallback((like: StoredLike) => {
    if (!wishlist.hasEmail) {
      pendingWishlistLikeRef.current = like
      wishlist.requestEmail()
      return
    }
    wishlist.addToWishlist(like.wcIds, like.totalPrice, like.funnelId)
    swiper.removeLike(like.key)
  }, [wishlist, swiper])

  const handleEmailSubmit = useCallback((email: string) => {
    wishlist.submitEmail(email)

    if (pendingSuperLikeRef.current) {
      const { wcIds, totalPrice, funnelId } = pendingSuperLikeRef.current
      pendingSuperLikeRef.current = null
      setTimeout(() => wishlist.addToWishlist(wcIds, totalPrice, funnelId), 100)
    }

    if (pendingWishlistLikeRef.current) {
      const like = pendingWishlistLikeRef.current
      pendingWishlistLikeRef.current = null
      setTimeout(() => {
        wishlist.addToWishlist(like.wcIds, like.totalPrice, like.funnelId)
        swiper.removeLike(like.key)
      }, 100)
    }
  }, [wishlist, swiper])

  const handleDislike = useCallback(() => handleSwipe('left'), [handleSwipe])
  const handleLike = useCallback(() => handleSwipe('right'), [handleSwipe])
  const handleSuperLike = useCallback(() => handleSwipe('up'), [handleSwipe])

  // Clicking a wishlist item → redirect to that funnel's configurator page
  const handleLoadWishlistConfig = useCallback((wcIds: number[], funnelSlug?: string) => {
    const configString = wcIds.join('-')
    const funnelUrls = configs[0]?.funnelUrls
    const pageUrl = funnelSlug && funnelUrls?.[funnelSlug]
    if (pageUrl) {
      window.location.href = `${pageUrl}?config=${configString}`
    }
  }, [configs])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-gray-400">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm">{ct('swLoading')}</p>
        </div>
      </div>
    )
  }

  if (isError || configs.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-gray-400">
        <p className="text-sm">{ct('swNotFound')}</p>
      </div>
    )
  }

  return (
    <div className="swiper-root relative w-full h-full overflow-hidden flex flex-col">
      {/* Card area - centered */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="swiper-stack relative w-full max-w-[800px] aspect-[1200/711]" style={{ touchAction: 'none' }}>
          <AnimatePresence mode="popLayout">
            {currentCombo && !swiper.isFinished && (
              <SwipeCard
                key={currentCombo.key}
                combination={currentCombo}
                baseImage={currentCombo.baseImage}
                onSwipe={handleSwipe}
                stackIndex={0}
                active={true}
              />
            )}
          </AnimatePresence>

          {swiper.isFinished && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 rounded-xl text-center p-6">
              <span className="text-5xl mb-4">&#x2728;</span>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {ct('swDone')}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {ct('swDoneText')}
                {swiper.likesCount > 0 && ` ${ct(swiper.likesCount > 1 ? 'swSavedN' : 'swSaved1', { n: swiper.likesCount })}`}
              </p>
              <button
                onClick={swiper.restart}
                className="px-5 py-2.5 bg-[#171717] hover:bg-black text-white text-sm font-medium rounded-lg transition-colors"
              >
                {ct('swRestart')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons - sticky bottom */}
      {!swiper.isFinished && (
        <div className="flex items-center justify-center gap-5 py-4 pb-6">
            <button
              onClick={handleDislike}
              className="w-14 h-14 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
              title={ct('swPass')}
              aria-label={ct('swPass')}
            >
              <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <button
              onClick={handleSuperLike}
              className="w-12 h-12 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
              title={ct('swSuper')}
              aria-label={ct('swSuper')}
            >
              <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </button>

            <button
              onClick={handleLike}
              className="w-14 h-14 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
              title={ct('swLike')}
              aria-label={ct('swLike')}
            >
              <svg className="w-7 h-7" fill="#171717" viewBox="0 0 24 24">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
            </button>
          </div>
        )}

      {/* Mobile wishlist text button - below action buttons */}
      {wishlist.hasEmail && wishlist.wishlists.length > 0 && (
        <button
          onClick={() => setMobileWishlistOpen(true)}
          className="lg:hidden w-full py-2 px-4 text-sm text-pink-500 font-medium flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
          {ct('myWishlist')} ({wishlist.wishlists.length}/{wishlist.maxItems})
        </button>
      )}

      {/* Likes drawer toggle - sticky right center */}
      <button
        onClick={() => setDrawerOpen(true)}
        className="fixed right-3 top-1/2 -translate-y-1/2 z-30 w-12 h-12 bg-white rounded-full shadow-lg border border-gray-100 flex items-center justify-center hover:shadow-xl transition-shadow"
      >
        <svg className="w-6 h-6" fill="#171717" viewBox="0 0 24 24">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
        {swiper.likesCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-pink-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {swiper.likesCount > 99 ? '99+' : swiper.likesCount}
          </span>
        )}
      </button>

      {/* Likes drawer */}
      <LikesDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        likes={swiper.likes}
        onAddToWishlist={handleDrawerAddToWishlist}
        onRemove={swiper.removeLike}
      />

      {/* Permanent wishlist sidebar - Desktop only */}
      <div className="hidden lg:block">
        <WishlistSidebar
          wishlists={wishlist.wishlists}
          onLoadConfig={handleLoadWishlistConfig}
          onDelete={wishlist.deleteWishlistItem}
          maxItems={wishlist.maxItems}
        />
      </div>

      {/* Mobile wishlist bottom sheet */}
      <AnimatePresence>
        {mobileWishlistOpen && (
          <>
            <motion.div
              className="lg:hidden fixed inset-0 bg-black/30 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileWishlistOpen(false)}
            />
            <motion.div
              className="lg:hidden fixed left-0 right-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-[0_-4px_20px_rgba(0,0,0,0.15)] flex flex-col"
              style={{ maxHeight: '70vh' }}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              {/* Header */}
              <div className="flex flex-col items-center pt-2 pb-3 border-b">
                <div className="w-10 h-1 bg-gray-300 rounded-full mb-2" />
                <div className="flex items-center justify-between w-full px-4">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <svg className="w-5 h-5 text-pink-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                    {ct('myWishlist')}
                    <span className="text-sm font-normal text-gray-500">({wishlist.wishlists.length}/{wishlist.maxItems})</span>
                  </h3>
                  <button
                    onClick={() => setMobileWishlistOpen(false)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Grid */}
              <div className="flex-1 overflow-y-auto p-3">
                <div className="grid grid-cols-3 gap-2">
                  {wishlist.wishlists.map(item => (
                    <div key={item.id} className="relative">
                      <div
                        onClick={() => {
                          handleLoadWishlistConfig(item.config, item.funnel_slug)
                          setMobileWishlistOpen(false)
                        }}
                        className={`w-full aspect-square bg-gray-100 rounded-lg overflow-hidden relative border-2 border-transparent ${
                          item.has_unavailable_products ? '' : 'hover:border-pink-400 cursor-pointer'
                        } transition-colors`}
                      >
                        {item.base_image && (
                          <img src={item.base_image} alt="" className={`absolute inset-0 w-full h-full object-contain ${item.has_unavailable_products ? 'opacity-40' : ''}`} />
                        )}
                        {item.product_images?.map(p => p.image && (
                          <img key={p.wcId} src={p.image} alt="" className={`absolute inset-0 w-full h-full object-contain ${item.has_unavailable_products ? 'opacity-40' : ''}`} style={{ zIndex: p.layer }} />
                        ))}
                        {item.has_unavailable_products && (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/30">
                            <p className="text-[9px] font-bold text-white drop-shadow">{ct('outOfStock')}</p>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); wishlist.deleteWishlistItem(item.id) }}
                        className="absolute -top-1 -right-1 z-50 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      >
                        x
                      </button>
                      <p className={`text-xs text-center mt-1 ${item.has_unavailable_products ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
                        {(item.total_price / 100).toFixed(0)}€
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Email popup */}
      {wishlist.showEmailPopup && (
        <WishlistEmailPopup
          onSubmit={handleEmailSubmit}
          onClose={() => {
            wishlist.setShowEmailPopup(false)
            pendingSuperLikeRef.current = null
            pendingWishlistLikeRef.current = null
          }}
        />
      )}
    </div>
  )
}
